import { marketBaseOf } from "@local/i18n/src/languages"

/**
 * Last-resort upsell id when `/api/upsell/get` returns nothing.
 *
 * Every id is the market+channel's **MULTI** upsell. The map has no type
 * dimension of its own, so a single-item cart that reaches the fallback still
 * lands on a multi offer — long-standing behaviour, not introduced here.
 *
 * Refreshed 2026-08-12 from `GET offer/ext/upsell/channel/<channel>/<market>/multi`
 * on offer.div.haus, then every resulting id re-fetched via
 * `GET offer/ext/upsell/<id>` and checked for `type: "MULTI"`, `active: true`,
 * and three non-empty slots. The previous ids had drifted from what the live
 * lookup returns; they were still valid records, but no longer the ones Offer
 * would pick. `DE` is new — the German pilot market (DEV-9539) had no row at
 * all, so every DE buyer whose lookup missed resolved `undefined`.
 *
 * Two rows deliberately do NOT follow the live lookup, because following it
 * would regress them:
 *
 * - `vest.US` — Offer has no `vest`/`us` upsell for either type (both 404). The
 *   id here is `baerskin-hoodie-4`'s US upsell, which is what US vest buyers
 *   have always been served. Left as-is; a real `vest`/`us` record should be
 *   configured in Offer, at which point this row should point at it.
 * - `GLOBAL` — `global` is effectively unconfigured in Offer (7 of 8
 *   channel/type combos 404). Every `GLOBAL` row therefore mirrors its
 *   channel's `EU` id, which is the arrangement that predates this refresh.
 *   `baerskin-hoodie-4` does have a real `global`/`multi` record
 *   (`up_01ao38fqn1g06`) but is kept on the EU mirror so all four channels
 *   follow one rule.
 *
 * This map duplicates mutable Offer config and will drift again. The durable
 * fix is a server-side fallback in `/api/upsell/get` rather than shipping ids
 * to the browser.
 */
export const getUpsellIdFallback = (market: string, channel: string) => {
  let upsellPerMarket
  switch (channel) {
    case "softshell":
      upsellPerMarket = {
        AU: "up_glg2fgy71yw0g",
        CA: "up_yf3046iwvht57",
        CH: "up_m0q3o6iqi7t8x",
        DE: "up_1a99581caac34",
        EU: "up_rti7wzpohsgkg",
        GB: "up_3h93mtvy6ayqd",
        GLOBAL: "up_rti7wzpohsgkg",
        NZ: "up_spg2u6zito5bt",
        US: "up_yogate4qgupbr",
      }
      break
    case "baerskin-hoodie-4":
      upsellPerMarket = {
        AU: "up_9i6qtd18dta90",
        CA: "up_5h62alndex38l",
        CH: "up_g6q5ocsio7xpk",
        DE: "up_e06b876b187e4",
        EU: "up_dko4nj0r9v2pv",
        GB: "up_uzouhhnpb75pv",
        GLOBAL: "up_dko4nj0r9v2pv",
        NZ: "up_vpoax79ztz91s",
        US: "up_o7csm5ngugqcc",
      }
      break
    case "vest":
      upsellPerMarket = {
        AU: "up_cglz8rdbajrb4",
        CA: "up_p51tav2u0usds",
        CH: "up_3gt3z9mc2d05t",
        DE: "up_f8d1c39bcd804",
        EU: "up_alerpromfr4ye",
        GB: "up_0fwvdxc8y6lc1",
        GLOBAL: "up_alerpromfr4ye",
        NZ: "up_zt7mxhqm3g8y1",
        // No vest/us record exists in Offer, so this row is frozen at the id
        // US vest buyers have always been served. It originated as a copy of
        // baerskin-hoodie-4's US id but does NOT track it — hoodie-4 moved to
        // up_o7csm5ngugqcc in the 2026-08-12 refresh and this stayed put.
        // Still verified active MULTI with three populated slots.
        US: "up_s9luzfu2n2ogx",
      }
      break
    case "store":
    default:
      upsellPerMarket = {
        AU: "up_ttjxqrslgqq4i",
        CA: "up_tkbkmq7k6yaew",
        CH: "up_pi9i5czjwinym",
        DE: "up_5a4d101b5bc44",
        EU: "up_fbz7jiide04o7",
        GB: "up_xyh68uhdz3cov",
        GLOBAL: "up_fbz7jiide04o7",
        NZ: "up_4f7g2zfa24sld",
        US: "up_x54cy6ir96sux",
      }
      break
  }
  // DEV-9850: `market` may be a public language-variant slug ("de-en") — this
  // is a market-registry lookup keyed on base markets.
  return upsellPerMarket[
    marketBaseOf(market).toUpperCase() as keyof typeof upsellPerMarket
  ]
}
